import React from 'react';
import Chip from '@mui/material/Chip';
import NoteTagList from '../theme/buttons/NoteTag';
import { useDoorCrewCard } from './DoorCrewCardContext';
import { TurfOrFallback } from './types';

function isTurf(t: TurfOrFallback | null | undefined): t is Extract<TurfOrFallback, { id: number }> {
  return t != null && typeof t === 'object' && 'id' in t && t.id !== undefined;
}

interface TurfSectionProps {
  turf: TurfOrFallback | null | undefined;
  layout?: 'inline' | 'sidebar';
}

export default function TurfSection({ turf, layout = 'inline' }: TurfSectionProps) {
  const { turfCodes, onOpenNoteModal, crew } = useDoorCrewCard();

  const turfcode = isTurf(turf)
    ? turfCodes.find((d) => d.id === turf.code)
    : undefined;

  const turfName = isTurf(turf) ? turf.name : null;
  const rawId = turf?._raw_id ?? (isTurf(turf) ? turf.id : undefined);
  const householdCount = isTurf(turf) ? turf.household_count : null;
  const aggData = isTurf(turf) ? turf.agg_data : null;
  const turfNotes = isTurf(turf) ? turf.turfnotes : [];
  const suppressed =
    isTurf(turf) &&
    turf.household_count_before_suppression != null &&
    turf.household_count_before_suppression > (turf.household_count ?? 0)
      ? turf.household_count_before_suppression - (turf.household_count ?? 0)
      : null;

  const handleNotesClick = () => {
    if (isTurf(turf)) {
      onOpenNoteModal(turf, crew.id);
    }
  };

  const labelClass =
    layout === 'sidebar' ? 'turf-label turf-label--sidebar' : 'turf-label';

  return (
    <div className={labelClass}>
      <div className="turf-name">
        {turfName ?? (
          rawId != null ? (
            <em style={{ opacity: 0.6 }}>Turf #{rawId}</em>
          ) : (
            <em style={{ opacity: 0.6 }}>Turf not found</em>
          )
        )}
      </div>

      {householdCount != null && (
        <div className="turf-agg-data">{householdCount} Doors</div>
      )}

      {aggData && (
        <div className="turf-agg-data primary-text">
          {aggData.households_attempted} doors /{' '}
          {aggData.targets_attempted} targets attempted.
        </div>
      )}

      {suppressed != null && (
        <div className="turf-agg-data danger-text turf-suppressed">
          <i className="fas fa-exclamation-triangle" />
          <b>{suppressed}</b> Doors Suppressed
        </div>
      )}

      {turfcode && (
        <Chip
          size="small"
          label={turfcode.name}
          color={turfcode.walkable ? 'default' : 'error'}
          variant="outlined"
          className="turf-code-chip"
        />
      )}

      <div className="notes-in-table turf-notes" onClick={handleNotesClick}>
        <NoteTagList addButton notes={turfNotes} />
      </div>
    </div>
  );
}
